"use client";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { usePageLoading } from "@/context/LoadingProvider";
import { Workspace } from "@/db/schema";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight, CalendarIcon, ChevronDown, Loader2 } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState, useTransition } from "react";

const createSearchString = (from: Date, to: Date) => {
  return `?from=${from.toISOString()}&to=${to.toISOString()}`;
};

export default function CalendarTools({
  workspace,
  from,
  to,
  datesWithEvents,
  groupName,
}: {
  workspace: Workspace;
  from: Date;
  to: Date;
  datesWithEvents: Date[];
  groupName: string;
}) {
  const router = useRouter();
  const [_, startTransition] = useTransition();
  const [selectedDate, setSelectedDate] = useState(from);
  const { isPageLoading, setPageLoading } = usePageLoading();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const selectDate = useCallback(
    (d1: Date) => {
      setPageLoading(true);
      const d2 = new Date(d1);
      d2.setHours(23, 59, 59, 999);
      setSelectedDate(d1);
      startTransition(() => {
        router.push(createSearchString(d1, d2));
      });
    },
    [router, setPageLoading],
  );

  const setToday = useCallback(() => {
    selectDate(today);
  }, [selectDate, today]);

  const isToday = (d: Date) => {
    const t = new Date();
    return (
      d.getDate() === t.getDate() &&
      d.getMonth() === t.getMonth() &&
      d.getFullYear() === t.getFullYear()
    );
  };

  const css = `
    .my-highlighted::after { 
      content: '';
      display: block;
      margin-top: 22px;
      height: 5px;
      width: 5px;
      background-color: black;
      border-radius: 50%;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }
  `;

  return (
    <div className="flex flex-row items-center justify-end gap-1 md:gap-2 p-2 md:p-3 bg-background border-b">
      {workspace?.imageUrl && (
        <Image
          width={35}
          height={35}
          src={workspace.imageUrl}
          alt={"logo"}
          className="mr-auto h-[25px] md:h-[35px] w-auto"
        />
      )}
      <h2 className="mr-auto pl-14 w-auto flex items-center heading-base">{groupName}</h2>
      <Button
        className="px-3 md:px-4 hidden sm:inline-flex"
        variant="outline"
        disabled={isToday(selectedDate)}
        onClick={setToday}
      >
        <span>Today</span>
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "md:min-w-[160px] gap-2 text-left font-normal px-3",
              !selectedDate && "text-muted-foreground",
            )}
          >
            {isPageLoading ? (
              <div>
                <Loader2 className="animate-spin h-4 w-4" />
              </div>
            ) : selectedDate ? (
              <>
                <CalendarIcon className="h-4 w-4 opacity-75" />
                {format(selectedDate, "EE, d MMM")}
              </>
            ) : (
              <span>Pick a date</span>
            )}
            <ChevronDown className="ml-auto h-4 w-4 opacity-25 hidden md:block" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <>
            <style>{css}</style>
            <Calendar
              weekStartsOn={1}
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (!date) return;
                selectDate(date);
              }}
              // disabled={(date) => date < today}
              initialFocus
              modifiersClassNames={{
                highlighted: "my-highlighted", // Apply custom class to highlighted days
              }}
              modifiers={{
                highlighted: datesWithEvents,
              }}
            />
          </>
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        size="icon"
        className="hidden sm:inline-flex"
        onClick={() => {
          const d = new Date(selectedDate);
          d.setDate(d.getDate() - 1);
          selectDate(d);
        }}
      >
        <ArrowLeft className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="hidden sm:inline-flex"
        onClick={() => {
          const d = new Date(selectedDate);
          d.setDate(d.getDate() + 1);
          selectDate(d);
        }}
      >
        <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );
}
